import {get, post} from "aws-amplify/api";
import React, {useCallback, useEffect, useRef} from "react";
import {useDropzone} from "react-dropzone";
import {Card, CardBody, CardHeader, CardTitle, Button, Row, Col} from "reactstrap";
import {BackgroundColorContext} from "../contexts/BackgroundColorContext";
import {useUser} from "../contexts/AuthContext";
import {usePayment} from "../contexts/PaymentContext";
import PageHeader from "../components/Common/PageHeader";
import AuthRequiredView from "../components/Common/AuthRequiredView";
import {
  uploadReducer,
  initialState,
  uploadingFile,
  processing,
  fetchingReport,
  success,
  error,
  start,
  webSocketProgress,
  webSocketComplete,
  webSocketError,
  setFile
} from "../hooks/UploadHandler";
import {useWebSocket, ProgressEvent, CompletionEvent, ErrorEvent} from "../hooks/useWebSocket";

import {RingLoader} from "react-spinners";
import {v4 as uuidv4} from "uuid";
import {uploadActionTypes} from "../constants";
import LogReport from "../components/Reports/LogReport";
import {titleCase} from "../utils/functions";
import UploadProgress from "../components/Upload/UploadProgress";

// Interface for checkTokens API response
interface TokenCheckResponse {
  hasEnoughTokens: boolean;
  tokensRequired: number;
  availableSource: "free_tier" | "paid_tokens" | "insufficient";
  currentBalance: {
    basicTokens: number;
    advancedTokens: number;
    freeUsageRemaining: number;
  };
  analysisType: "basic" | "advanced";
}

// Type guard function for token check response
function isTokenCheckResponse(obj: any): obj is TokenCheckResponse {
  return (
    obj &&
    typeof obj.hasEnoughTokens === "boolean" &&
    typeof obj.tokensRequired === "number" &&
    typeof obj.availableSource === "string" &&
    obj.currentBalance &&
    typeof obj.currentBalance.basicTokens === "number" &&
    typeof obj.currentBalance.advancedTokens === "number" &&
    typeof obj.currentBalance.freeUsageRemaining === "number" &&
    typeof obj.analysisType === "string"
  );
}
const Upload = () => {
  const [reducerState, dispatch] = React.useReducer(uploadReducer, initialState);
  const {user, refreshJwt} = useUser();
  const {getTokenBalance} = usePayment();
  const timezoneOffset: number = user?.settings?.timezoneOffsetMinutes ?? 0;
  const sessionId = useRef<string>(uuidv4());
  const webSocketTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAuthenticated = user?.authenticated === true;

  // WebSocket hook for real-time progress updates
  const {
    isConnected,
    lastMessage,
    connect,
    disconnect,
    error: wsError,
    isReconnecting,
    reconnectAttempts
  } = useWebSocket();

  // Clear WebSocket timeout
  const clearWebSocketTimeout = () => {
    if (webSocketTimeoutRef.current) {
      clearTimeout(webSocketTimeoutRef.current);
      webSocketTimeoutRef.current = null;
    }
  };

  // Start WebSocket timeout for no message received scenario
  const startWebSocketTimeout = () => {
    clearWebSocketTimeout(); // Clear any existing timeout
    webSocketTimeoutRef.current = setTimeout(() => {
      //console.warn("No WebSocket messages received within 15 seconds, timing out...");
      disconnect();
      dispatch(
        error({
          response: {
            error:
              "Processing appears to be stuck. Please try uploading your file again, or check back later if the issue persists."
          }
        })
      );
    }, 45000); // 45 seconds - allows for Lambda cold starts and SQS processing delay
  };

  function reset() {
    dispatch(start());
    clearWebSocketTimeout(); // Clear any existing timeout
    disconnect(); // Disconnect WebSocket on reset
  }

  // Handle WebSocket messages
  useEffect(() => {
    if (!lastMessage) {
      return;
    }

    // Clear timeout when we receive any message
    clearWebSocketTimeout();

    switch (lastMessage.type) {
      case "progress": {
        const progressEvent = lastMessage.data as ProgressEvent;
        dispatch(webSocketProgress(progressEvent, isConnected));
        break;
      }

      case "complete":
        {
          const completionEvent = lastMessage.data as CompletionEvent;
          // Extract userId and logId from fileName to fetch the actual report
          if (completionEvent.fileName && user) {
            const [userId, fileNameWithExt] = completionEvent.fileName.split("/");
            const logId = fileNameWithExt.replace(".gz", "");

            // Fetch the actual report data
            dispatch(fetchingReport());
            const params = {
              headers: {
                Authorization: user.jwtToken
              }
            };

            getLogReport(params, userId, logId).then((reportData) => {
              if (reportData) {
                dispatch(success(reportData));
                // Refresh token balance after successful upload
                getTokenBalance();
              } else {
                dispatch(error({response: {error: "Failed to fetch report data"}}));
              }
            });
          } else {
            // Fallback to just completing without report data
            dispatch(webSocketComplete({reportUrl: completionEvent.reportUrl}));
            // Refresh token balance after successful upload
            getTokenBalance();
          }
        }
        break;

      case "error":
        {
          const errorEvent = lastMessage.data as ErrorEvent;
          dispatch(webSocketError({}, errorEvent.error));
        }
        break;
    }
  }, [lastMessage, isConnected, user, getTokenBalance]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearWebSocketTimeout();
    };
  }, []);

  function readFileAsync(file: any) {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = reject;

      reader.readAsText(file);
    });
  }

  async function getLogReport(params: any, userId: string, logId: string) {
    try {
      const {body} = await get({
        apiName: "app",
        path: `/report/${userId}/${logId}`,
        options: params
      }).response;
      const result: any = await body.json();

      // Handle large files that return a pre-signed S3 URL
      if (result && typeof result === "object" && result.useSignedUrl && result.url) {
        //console.log(`Large file detected (${result.fileSizeMB}MB), fetching from S3...`);
        const s3Response = await fetch(result.url);
        return await s3Response.json();
      }

      // Small files return data directly
      return result;
    } catch (err) {
      //console.error(`error getting log report: ${err}`);
      dispatch(error({response: {err}}));
    }
  }

  const callbackFunction = async (acceptedFiles: any) => {
    if (acceptedFiles.length !== 1) {
      dispatch(error({response: {error: "exactly one file must be selected"}}));
      return;
    }

    //console.log("Upload callback function: File Accepted");
    //console.log("file", acceptedFiles[0]);

    const fileInfo: any = {
      lastModified: acceptedFiles[0].lastModified,
      lastModifiedDate: acceptedFiles[0].lastModifiedDate,
      name: acceptedFiles[0].name,
      path: acceptedFiles[0].path,
      size: acceptedFiles[0].size,
      type: acceptedFiles[0].type,
      webkitRelativePath: acceptedFiles[0].webkitRelativePath
    };

    dispatch(setFile(fileInfo));

    // Calculate file size in MB and tokens required
    const fileSizeMB = acceptedFiles[0].size / (1024 * 1024);
    const tokensRequired = Math.ceil(fileSizeMB / 5); // 1 token per 5MB, rounded up

    if (user === undefined) {
      dispatch(error({response: {error: "user is not signed in"}}));
      return;
    }

    await refreshJwt(user);

    const params: any = {
      headers: {
        Authorization: user.jwtToken
      }
    };
    const userId: string = user.identityId as string;

    // Check if user has enough tokens before proceeding
    dispatch(uploadingFile("Checking token availability..."));

    try {
      const response = await post({
        apiName: "app",
        path: "/tokens/check",
        options: {
          ...params,
          body: {
            analysisType: "basic",
            logSizeMB: fileSizeMB
          }
        }
      }).response;

      // Check if response indicates insufficient tokens (402)
      if (response.statusCode === 402 || response.statusCode === 402) {
        let errorMessage = `Insufficient tokens. You need ${tokensRequired} token${
          tokensRequired > 1 ? "s" : ""
        } to upload this file. Please purchase more tokens to continue.`;

        try {
          const errorData: any = await response.body.json();
          // New structured error format: { error: { code, message, statusCode, details } }
          if (errorData?.error?.details?.currentBalance) {
            const balance = errorData.error.details.currentBalance;
            const totalAvailable = balance.basicTokens + balance.freeUsageRemaining;
            const onlyText = totalAvailable > 0 ? "only have" : "have";
            errorMessage = `Insufficient tokens. You need ${tokensRequired} token${
              tokensRequired > 1 ? "s" : ""
            } but ${onlyText} ${totalAvailable} available (${balance.basicTokens} paid + ${
              balance.freeUsageRemaining
            } free). Please purchase more tokens to continue.`;
          }
        } catch {
          //console.warn("Failed to parse 402 error response:", parseErr);
        }

        dispatch(error({response: {error: errorMessage}}));
        return;
      }

      let tokenCheckResponse: any = null;
      try {
        tokenCheckResponse = await response.body.json();
      } catch {
        //console.error("Failed to parse token check response as JSON:", jsonParseErr);
        dispatch(error({response: {error: "Failed to verify token balance. Please try again."}}));
        return;
      }

      if (!isTokenCheckResponse(tokenCheckResponse)) {
        //console.warn("Invalid token check response, proceeding with upload (backend will validate)");
      } else {
        const tokenCheck = tokenCheckResponse;

        if (!tokenCheck.hasEnoughTokens) {
          // Calculate total available tokens for error message
          const availableBasic = tokenCheck.currentBalance.basicTokens;
          const availableFree = tokenCheck.currentBalance.freeUsageRemaining;
          const totalAvailable = availableBasic + availableFree;
          const onlyText = totalAvailable > 0 ? "only have" : "have";

          dispatch(
            error({
              response: {
                error: `Insufficient tokens. You need ${tokensRequired} token${
                  tokensRequired > 1 ? "s" : ""
                } but ${onlyText} ${totalAvailable} available (${availableBasic} paid + ${availableFree} free). Please purchase more tokens to continue.`
              }
            })
          );
          return;
        }

        // If more than 1 token required, show confirmation dialog
        if (tokensRequired > 1) {
          const availableBasic = tokenCheck.currentBalance.basicTokens;
          const availableFree = tokenCheck.currentBalance.freeUsageRemaining;
          const totalAvailable = availableBasic + availableFree;
          const sourceInfo = tokenCheck.availableSource === "free_tier" ? "using free tier" : "using paid tokens";

          const confirmed = window.confirm(
            `This upload will consume ${tokensRequired} tokens (${fileSizeMB.toFixed(
              1
            )} MB file) ${sourceInfo}.\n\nYou currently have ${totalAvailable} tokens available (${availableBasic} paid + ${availableFree} free).\n\nDo you want to proceed with the upload?`
          );

          if (!confirmed) {
            dispatch(start()); // Reset to initial state
            return;
          }
        }
      }
    } catch (err: any) {
      //console.warn("Token check failed:", err);

      // Check if the error is a 402 (insufficient tokens) from AWS Amplify
      const is402Error =
        err?.response?.statusCode === 402 ||
        err?._response?.statusCode === 402 ||
        err?.$metadata?.httpStatusCode === 402;

      if (is402Error) {
        let errorMessage = `Insufficient tokens. You need ${tokensRequired} token${
          tokensRequired > 1 ? "s" : ""
        } to upload this file. Please purchase more tokens to continue.`;

        try {
          // Try to parse error details from AWS Amplify error structure
          let errorData: any = null;

          // Try different possible locations for the error data
          if (err?._response?.body) {
            try {
              errorData = JSON.parse(err._response.body);
            } catch {
              //console.warn("Failed to parse _response.body JSON:", parseErr);
            }
          } else if (err?.response?.data) {
            errorData = err.response.data;
          } else if (err?.data) {
            errorData = err.data;
          }

          if (errorData?.error?.details?.currentBalance) {
            const balance = errorData.error.details.currentBalance;
            const totalAvailable = balance.basicTokens + balance.freeUsageRemaining;
            const onlyText = totalAvailable > 0 ? "only have" : "have";
            errorMessage = `Insufficient tokens. You need ${tokensRequired} token${
              tokensRequired > 1 ? "s" : ""
            } but ${onlyText} ${totalAvailable} available (${balance.basicTokens} paid + ${
              balance.freeUsageRemaining
            } free). Please purchase more tokens to continue.`;
          }
        } catch {
          //console.warn("Failed to parse 402 error response:", parseErr);
        }

        dispatch(error({response: {error: errorMessage}}));
        return;
      }

      // For other API errors, show confirmation dialog for large files
      if (tokensRequired > 1) {
        const confirmed = window.confirm(
          `This upload will consume ${tokensRequired} tokens (${fileSizeMB.toFixed(
            1
          )} MB file).\n\nDo you want to proceed with the upload?\n\nNote: If you don't have enough tokens, the upload will fail.`
        );

        if (!confirmed) {
          dispatch(start()); // Reset to initial state
          return;
        }
      }
    }

    // Proceed with upload
    dispatch(uploadingFile("Preparing upload..."));

    //console.log("Uploading the data");

    async function getUploadUrl(params: any, userId: string, logId: string, webSocketConnectionId: string | null) {
      try {
        const requestBody: any = webSocketConnectionId
          ? {
              connectionId: webSocketConnectionId,
              sessionId: sessionId.current
            }
          : undefined;

        const {body} = await post({
          apiName: "app",
          path: `/signedUrl/${userId}/${logId}/${timezoneOffset}`,
          options: {
            ...params,
            body: requestBody
          }
        }).response;
        return await body.json();
      } catch {
        //console.log(`error getting upload url: ${err}`);
        return undefined;
      }
    }

    // signed headers are: content-encoding;host;x-amz-server-side-encryption
    async function uploadFile(fileHandle: any, signedUploadUrl: string) {
      try {
        const fileContents: any = await readFileAsync(fileHandle);
        const headers: Record<string, string> = {
          //"content-type": "text/plain",
          "content-encoding": "gzip",
          "x-amz-server-side-encryption": "AES256"
        };

        // WebSocket connection info is now stored in DynamoDB, not S3 metadata

        return await fetch(signedUploadUrl, {
          method: "PUT",
          body: fileContents,
          headers
        });
      } catch {
        //console.error(`error uploading file: ${err}`);
        return undefined;
      }
    }

    const logId: string = uuidv4();
    let response: any = null;

    try {
      // Connect to WebSocket before starting upload and get connection ID
      dispatch(uploadingFile("Connecting to live updates..."));
      const webSocketConnectionId: string | null = await connect();

      if (!webSocketConnectionId) {
        clearWebSocketTimeout(); // Clear timeout on error
        dispatch(error({response: {error: "Failed to establish WebSocket connection"}}));
        return;
      }

      dispatch(uploadingFile("Getting upload authorization..."));
      response = await getUploadUrl(params, userId, logId, webSocketConnectionId);

      if (response === undefined) {
        clearWebSocketTimeout(); // Clear timeout on error
        dispatch(error({response: {error: "failed to get upload url"}}));
        return;
      }

      dispatch(uploadingFile("Uploading file to server..."));

      response = await uploadFile(acceptedFiles[0], response.signedUrl);

      if (response === undefined) {
        clearWebSocketTimeout(); // Clear timeout on error
        dispatch(error({response: {error: "failed to upload file"}}));
        return;
      }

      // Switch to processing mode - WebSocket will handle all updates from here
      dispatch(processing());

      // Start timeout to handle case where no WebSocket messages are received
      startWebSocketTimeout();
    } catch {
      //console.log(`error upload and parse log file: ${err}`);
      clearWebSocketTimeout(); // Clear timeout on error
      dispatch(error({response: {error: "failed to upload and parse log file"}}));
      return;
    }
  };

  const onDrop = useCallback(callbackFunction, [
    timezoneOffset,
    user,
    refreshJwt,
    connect,
    disconnect,
    startWebSocketTimeout,
    clearWebSocketTimeout,
    getTokenBalance
  ]);

  const {getRootProps, getInputProps, isDragActive} = useDropzone({onDrop});

  const dropboxStatus: string = isDragActive ? "active" : "default";

  return (
    <BackgroundColorContext.Consumer>
      {({color}) => (
        <>
          <PageHeader
            title="Upload & Analyze"
            subtitle={
              isAuthenticated
                ? "Drop your log file below to analyze your session performance"
                : "Sign in to start analyzing your virtual cycling set-up performance"
            }
            icon="fa fa-upload"
          />
          <Row>
            <Col md="12">
              {/* Show marketing section for non-authenticated users */}
              {!isAuthenticated && (
                <AuthRequiredView
                  title="Ready to Analyze Your Virtual Cycling Set-up Performance?"
                  subtitle="Sign in to upload your log file and get instant expert-level diagnostics"
                />
              )}

              {/* Main upload content for authenticated users */}
              {isAuthenticated && (
                <>
                  {[uploadActionTypes.SUCCESS, uploadActionTypes.ERROR, uploadActionTypes.NOT_A_LOG_FILE].includes(
                    reducerState.type
                  ) && (
                    <Card>
                      <CardHeader data-color={color} className="no-body">
                        <Row>
                          <Col md="6" className="d-flex align-items-center">
                            <CardTitle tag="h4">
                              Log Parser Status - {titleCase(reducerState.type).replace(/_/g, " ")}
                            </CardTitle>
                          </Col>
                          <Col md="6" className="d-flex justify-content-end">
                            <Button color="primary" data-color={color} onClick={reset}>
                              Reset <i className="fa fa-redo-alt" />
                            </Button>
                          </Col>
                        </Row>
                      </CardHeader>
                    </Card>
                  )}
                  {reducerState.type === uploadActionTypes.START && (
                    <Card>
                      <CardBody>
                        <div className={`dropbox ${dropboxStatus}`} {...getRootProps()}>
                          <div>
                            <input {...getInputProps()} />

                            <div className="iconWrapper">
                              <i className="fa fa-upload"></i>
                            </div>

                            {isDragActive ? (
                              <>
                                <p className="heading">Drop Log.txt file here</p>
                                <p className="subHeading">
                                  For Zwift, the Log.txt is in \Users\AppData\Local\Zwift\ on Windows and
                                  Documents\Zwift\Logs on Mac
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="heading">Drop Log.txt file here or click to browse file system</p>
                                <p className="subHeading">
                                  For Zwift, the Log.txt is in \Users\AppData\Local\Zwift\ on Windows and
                                  Documents\Zwift\Logs on Mac
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {[uploadActionTypes.UPLOADING, uploadActionTypes.PROCESSING, uploadActionTypes.FETCHING].includes(
                    reducerState.type
                  ) && (
                    <Card>
                      <CardBody>
                        <div className="dropbox default">
                          {reducerState.isWebSocketActive && reducerState.progressEvent ? (
                            // Show progress bar with WebSocket data
                            <UploadProgress
                              progress={reducerState.progressEvent.progress}
                              message={reducerState.progressEvent.message}
                              stage={reducerState.progressEvent.stage}
                              progressEvent={reducerState.progressEvent}
                              isWebSocketConnected={isConnected}
                              isReconnecting={isReconnecting}
                              reconnectAttempts={reconnectAttempts}
                            />
                          ) : reducerState.isWebSocketActive && !reducerState.progressEvent ? (
                            // WebSocket is active but no progress data yet
                            <>
                              <div className="iconWrapper">
                                <RingLoader color="#4682b4" size={80} loading={true} />
                              </div>
                              <p className="statusMessage">
                                {isReconnecting
                                  ? `Reconnecting... (attempt ${reconnectAttempts + 1})`
                                  : isConnected
                                    ? "Connected - waiting for progress updates..."
                                    : "Connecting to live updates..."}
                              </p>
                              {wsError && !isReconnecting && (
                                <small className="text-warning d-block mt-2">WebSocket: {wsError}</small>
                              )}
                              {isReconnecting && (
                                <small className="text-info d-block mt-2">
                                  Connection lost - automatically reconnecting...
                                </small>
                              )}
                            </>
                          ) : reducerState.isProcessing ? (
                            // Show traditional spinner
                            <>
                              <div className="iconWrapper">
                                <RingLoader color="#4682b4" size={80} loading={true} />
                              </div>
                              <p className="statusMessage">{reducerState.message}</p>
                            </>
                          ) : (
                            <p className="statusMessage">{reducerState.message}</p>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {[uploadActionTypes.ERROR, uploadActionTypes.NOT_A_LOG_FILE].includes(reducerState.type) && (
                    <Card>
                      <CardBody>
                        <div className="dropbox default">
                          <div className="iconWrapper">
                            <i className="fa fa-exclamation-triangle"></i>
                          </div>
                          <p className="statusMessage">
                            {reducerState.type === uploadActionTypes.ERROR && reducerState.message}
                            {reducerState.type === uploadActionTypes.NOT_A_LOG_FILE && reducerState.message}
                          </p>
                        </div>
                      </CardBody>
                    </Card>
                  )}

                  {reducerState.type === uploadActionTypes.SUCCESS &&
                    (reducerState.response.activities === undefined ||
                      reducerState.response.activities.length === 0) && (
                      <Card>
                        <CardBody>
                          <div className="dropbox default">
                            <div className="iconWrapper">
                              <i className="fa fa-exclamation-triangle"></i>
                            </div>
                            <p className="statusMessage">No data found. Are you sure the file was a game log file?</p>
                          </div>
                        </CardBody>
                      </Card>
                    )}

                  {reducerState.type === uploadActionTypes.SUCCESS &&
                    reducerState.response.activities &&
                    reducerState.response.activities.length > 0 && (
                      <LogReport {...reducerState.response} isShareable={true} logId={reducerState.response.id} />
                    )}
                </>
              )}
            </Col>
          </Row>
        </>
      )}
    </BackgroundColorContext.Consumer>
  );
};

export default Upload;
